import { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Plus, Edit, Trash2, Search, FileText, X, Code, Eye } from 'lucide-react'
import toast from 'react-hot-toast'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table'
import { Badge } from '../components/ui/Badge'
import { enumsApi, brandsApi, modelsApi } from '../lib/api'
import { useLanguage } from '../contexts/LanguageContext'

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'align': [] }],
    ['blockquote'],
    ['link'],
    ['clean']
  ]
}

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'align', 'blockquote', 'link'
]

// Russian differs per domain: base `ru` is carify.az, `ru_kz`/`ru_kg` override the
// Russian copy on carify-kz.com / carify-kg.com (empty → falls back to base `ru`).
const languageLabels = {
  az: 'Azərbaycan',
  ru: 'Русский (carify.az)',
  ru_kz: 'Русский (carify-kz.com)',
  ru_kg: 'Русский (carify-kg.com)',
  en: 'English',
  ko: '한국어',
  kk: 'Қазақша',
  ky: 'Кыргызча'
}

const currentYear = new Date().getFullYear()
const yearOptions = Array.from({ length: 30 }, (_, i) => currentYear - i)

const emptyForm = {
  title_az: '', title_en: '', title_ru: '', title_ru_kz: '', title_ru_kg: '', title_ko: '', title_kk: '', title_ky: '',
  content_az: '', content_en: '', content_ru: '', content_ru_kz: '', content_ru_kg: '', content_ko: '', content_kk: '', content_ky: '',
  meta_title_az: '', meta_title_en: '', meta_title_ru: '', meta_title_ru_kz: '', meta_title_ru_kg: '', meta_title_ko: '', meta_title_kk: '', meta_title_ky: '',
  meta_description_az: '', meta_description_en: '', meta_description_ru: '', meta_description_ru_kz: '', meta_description_ru_kg: '', meta_description_ko: '', meta_description_kk: '', meta_description_ky: '',
  filters: []
}

const emptyFilter = {
  manufacturer_id: '', model_group_id: '', year: '', fuel_type_code: '', vehicle_type_id: '',
  color_id: '', sale_status_id: ''
}

export default function SeoTexts() {
  const [listFilters, setListFilters] = useState({ search: '', page: 1, limit: 20 })
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState(emptyForm)
  const [expandedRow, setExpandedRow] = useState(null)
  const [activeTab, setActiveTab] = useState('content')
  const [htmlMode, setHtmlMode] = useState({})
  const { currentLanguage } = useLanguage()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery(
    ['seo-texts', listFilters],
    () => enumsApi.seoTexts.getAll(listFilters),
    { select: (d) => d?.data?.data, keepPreviousData: true, retry: 1, refetchOnWindowFocus: false }
  )

  const { data: brandsData } = useQuery(
    'brands-for-seo',
    () => brandsApi.getAll({ limit: 300 }),
    { select: (d) => d?.data?.data?.results || d?.data?.results || [] }
  )

  const { data: fuelTypesData } = useQuery(
    'fuel-types-for-seo',
    () => enumsApi.fuelTypes.getAll({ limit: 100 }),
    { select: (d) => d?.data?.data?.results || [] }
  )

  const { data: vehicleTypesData } = useQuery(
    'vehicle-types-for-seo',
    () => enumsApi.vehicleTypes.getAll({ limit: 100 }),
    { select: (d) => d?.data?.data?.results || [] }
  )

  const { data: colorsData } = useQuery(
    'colors-for-seo',
    () => enumsApi.colors.getAll({ limit: 200 }),
    { select: (d) => d?.data?.data?.results || [] }
  )

  const { data: saleStatusesData } = useQuery(
    'sale-statuses-for-seo',
    () => enumsApi.saleStatuses.getAll({ limit: 50 }),
    { select: (d) => d?.data?.data?.results || [] }
  )

  const createMutation = useMutation(
    (data) => enumsApi.seoTexts.create(data),
    {
      onSuccess: () => {
        toast.success('SEO text created successfully')
        queryClient.invalidateQueries(['seo-texts'])
        closeForm()
      }
    }
  )

  const updateMutation = useMutation(
    (data) => enumsApi.seoTexts.update(editingId, data),
    {
      onSuccess: () => {
        toast.success('SEO text updated successfully')
        queryClient.invalidateQueries(['seo-texts'])
        closeForm()
      }
    }
  )

  const deleteMutation = useMutation(
    (id) => enumsApi.seoTexts.delete(id),
    {
      onSuccess: () => {
        toast.success('SEO text deleted successfully')
        queryClient.invalidateQueries(['seo-texts'])
      }
    }
  )

  const closeForm = () => {
    setShowForm(false)
    setEditingId(null)
    setFormData(emptyForm)
    setActiveTab('content')
  }

  const handleEdit = (seoText) => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setEditingId(seoText.id)
    setFormData({
      title_az: seoText.title_az || '',
      title_en: seoText.title_en || '',
      title_ru: seoText.title_ru || '',
      title_ru_kz: seoText.title_ru_kz || '',
      title_ru_kg: seoText.title_ru_kg || '',
      title_ko: seoText.title_ko || '',
      title_kk: seoText.title_kk || '',
      title_ky: seoText.title_ky || '',
      content_az: seoText.content_az || '',
      content_en: seoText.content_en || '',
      content_ru: seoText.content_ru || '',
      content_ru_kz: seoText.content_ru_kz || '',
      content_ru_kg: seoText.content_ru_kg || '',
      content_ko: seoText.content_ko || '',
      content_kk: seoText.content_kk || '',
      content_ky: seoText.content_ky || '',
      meta_title_az: seoText.meta_title_az || '',
      meta_title_en: seoText.meta_title_en || '',
      meta_title_ru: seoText.meta_title_ru || '',
      meta_title_ru_kz: seoText.meta_title_ru_kz || '',
      meta_title_ru_kg: seoText.meta_title_ru_kg || '',
      meta_title_ko: seoText.meta_title_ko || '',
      meta_title_kk: seoText.meta_title_kk || '',
      meta_title_ky: seoText.meta_title_ky || '',
      meta_description_az: seoText.meta_description_az || '',
      meta_description_en: seoText.meta_description_en || '',
      meta_description_ru: seoText.meta_description_ru || '',
      meta_description_ru_kz: seoText.meta_description_ru_kz || '',
      meta_description_ru_kg: seoText.meta_description_ru_kg || '',
      meta_description_ko: seoText.meta_description_ko || '',
      meta_description_kk: seoText.meta_description_kk || '',
      meta_description_ky: seoText.meta_description_ky || '',
      filters: (seoText.filters || []).map(f => ({
        manufacturer_id: f.manufacturer_id || '',
        model_group_id: f.model_group_id || '',
        year: f.year || '',
        fuel_type_code: f.fuel_type_code || '',
        vehicle_type_id: f.vehicle_type_id || '',
        color_id: f.color_id || '',
        sale_status_id: f.sale_status_id || ''
      }))
    })
    setShowForm(true)
  }

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this SEO text?')) {
      deleteMutation.mutate(id)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const payload = {
      ...formData,
      filters: formData.filters.map(f => ({
        manufacturer_id: f.manufacturer_id || null,
        model_group_id: f.model_group_id || null,
        year: f.year || null,
        fuel_type_code: f.fuel_type_code || null,
        vehicle_type_id: f.vehicle_type_id || null,
        color_id: f.color_id || null,
        sale_status_id: f.sale_status_id || null
      }))
    }
    if (editingId) {
      updateMutation.mutate(payload)
    } else {
      createMutation.mutate(payload)
    }
  }

  const addFilter = () => {
    setFormData(prev => ({
      ...prev,
      filters: [...prev.filters, { ...emptyFilter }]
    }))
  }

  const removeFilter = (index) => {
    setFormData(prev => ({
      ...prev,
      filters: prev.filters.filter((_, i) => i !== index)
    }))
  }

  const updateFilter = (index, field, value) => {
    setFormData(prev => {
      const newFilters = [...prev.filters]
      newFilters[index] = { ...newFilters[index], [field]: value }
      if (field === 'manufacturer_id') {
        newFilters[index].model_group_id = ''
      }
      return { ...prev, filters: newFilters }
    })
  }

  const renderFilterBadges = (filters) => {
    if (!filters || filters.length === 0) return <span className="text-gray-400 text-xs">No filters</span>
    return (
      <div className="flex flex-wrap gap-1">
        {filters.map((f, i) => {
          const parts = []
          if (f.manufacturer_name) parts.push(f.manufacturer_name)
          if (f.model_name) parts.push(f.model_name)
          if (f.year) parts.push(f.year)
          if (f.fuel_type_code) parts.push(f.fuel_type_code)
          if (f.vehicle_type_name) parts.push(f.vehicle_type_name)
          if (f.color_name) parts.push(f.color_name)
          if (f.sale_status_name) parts.push(f.sale_status_name)
          return (
            <Badge key={i} variant="default" className="text-xs">
              {parts.length > 0 ? parts.join(' / ') : 'Empty filter'}
            </Badge>
          )
        })}
      </div>
    )
  }

  const tabs = [
    { id: 'content', label: 'Content' },
    { id: 'meta', label: 'Meta Tags' },
    { id: 'filters', label: 'Filter Combinations' },
  ]

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SEO Texts</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage SEO content for filter combinations on the catalog page
          </p>
        </div>
        <Button onClick={() => { setShowForm(true); setFormData(emptyForm); setEditingId(null) }}>
          <Plus className="h-4 w-4 mr-2" />
          Add SEO Text
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>{editingId ? 'Edit SEO Text' : 'Create SEO Text'}</CardTitle>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex space-x-1 mt-4 border-b border-gray-200">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                  {tab.label}
                  {tab.id === 'filters' && formData.filters.length > 0 && (
                    <span className="ml-1.5 bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded-full text-xs">
                      {formData.filters.length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">

              {activeTab === 'content' && (
                <>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Title</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(languageLabels).map(([lang, label]) => (
                        <div key={lang}>
                          <label className="label">
                            Title ({label})
                            {lang === currentLanguage && (
                              <span className="ml-2 text-xs text-primary-600 font-medium">(Current)</span>
                            )}
                          </label>
                          <Input
                            placeholder={`Title in ${label}`}
                            value={formData[`title_${lang}`]}
                            onChange={(e) => setFormData(prev => ({ ...prev, [`title_${lang}`]: e.target.value }))}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Content (SEO Text)</h3>
                    <div className="grid grid-cols-1 gap-4">
                      {Object.entries(languageLabels).map(([lang, label]) => {
                        const isHtml = !!htmlMode[lang]
                        return (
                          <div key={lang}>
                            <div className="flex justify-between items-center mb-1">
                              <label className="label mb-0">
                                Content ({label})
                                {lang === currentLanguage && (
                                  <span className="ml-2 text-xs text-primary-600 font-medium">(Current)</span>
                                )}
                              </label>
                              <button
                                type="button"
                                onClick={() => setHtmlMode(prev => ({ ...prev, [lang]: !prev[lang] }))}
                                className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
                              >
                                {isHtml ? <><Eye className="h-3 w-3" /> Visual</> : <><Code className="h-3 w-3" /> HTML</>}
                              </button>
                            </div>
                            {isHtml ? (
                              <textarea
                                className="input font-mono text-sm min-h-[240px]"
                                placeholder={`<p>Your HTML content in ${label}</p>`}
                                value={formData[`content_${lang}`] || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, [`content_${lang}`]: e.target.value }))}
                                spellCheck={false}
                              />
                            ) : (
                              <div className="seo-editor-wrapper">
                                <ReactQuill
                                  theme="snow"
                                  value={formData[`content_${lang}`] || ''}
                                  onChange={(value) => setFormData(prev => ({ ...prev, [`content_${lang}`]: value }))}
                                  modules={quillModules}
                                  formats={quillFormats}
                                  placeholder={`SEO content in ${label} (2000-3000 characters recommended)`}
                                />
                              </div>
                            )}
                            <p className="text-xs text-gray-400 mt-1">
                              {(formData[`content_${lang}`] || '').replace(/<[^>]*>/g, '').length} characters (text only)
                            </p>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </>
              )}

              {activeTab === 'meta' && (
                <>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Meta Title</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(languageLabels).map(([lang, label]) => (
                        <div key={lang}>
                          <label className="label">Meta Title ({label})</label>
                          <Input
                            placeholder={`Meta title in ${label}`}
                            value={formData[`meta_title_${lang}`]}
                            onChange={(e) => setFormData(prev => ({ ...prev, [`meta_title_${lang}`]: e.target.value }))}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Meta Description</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(languageLabels).map(([lang, label]) => (
                        <div key={lang}>
                          <label className="label">Meta Description ({label})</label>
                          <textarea
                            className="input min-h-[80px]"
                            placeholder={`Meta description in ${label}`}
                            value={formData[`meta_description_${lang}`]}
                            onChange={(e) => setFormData(prev => ({ ...prev, [`meta_description_${lang}`]: e.target.value }))}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {activeTab === 'filters' && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Filter Combinations</h3>
                      <p className="text-sm text-gray-500">
                        Assign this SEO text to one or more filter combinations. Each combination defines which catalog page will display this text.
                      </p>
                    </div>
                    <Button type="button" variant="outline" onClick={addFilter}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Combination
                    </Button>
                  </div>

                  {formData.filters.length === 0 ? (
                    <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                      <FileText className="mx-auto h-8 w-8 text-gray-400" />
                      <p className="mt-2 text-sm text-gray-500">No filter combinations yet</p>
                      <Button type="button" variant="outline" className="mt-3" onClick={addFilter}>
                        <Plus className="h-4 w-4 mr-1" />
                        Add First Combination
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {formData.filters.map((filter, index) => (
                        <FilterCombinationRow
                          key={index}
                          filter={filter}
                          index={index}
                          brands={brandsData || []}
                          fuelTypes={fuelTypesData || []}
                          vehicleTypes={vehicleTypesData || []}
                          colors={colorsData || []}
                          saleStatuses={saleStatusesData || []}
                          currentLanguage={currentLanguage}
                          onUpdate={updateFilter}
                          onRemove={removeFilter}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                <Button type="button" variant="outline" onClick={closeForm}>
                  Cancel
                </Button>
                <Button type="submit" loading={createMutation.isLoading || updateMutation.isLoading}>
                  {editingId ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Search className="h-5 w-5 mr-2" />
            Search SEO Texts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-w-md">
            <Input
              placeholder="Search by title or content..."
              value={listFilters.search}
              onChange={(e) => setListFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>SEO Texts ({data?.count || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-center py-12">
              <p className="text-red-600">Error loading SEO texts: {error.message}</p>
            </div>
          ) : isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : data?.results?.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Filter Combinations</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.results.map((seo) => (
                    <TableRow key={seo.id}>
                      <TableCell>
                        <div className="max-w-xs">
                          <p className="font-medium text-gray-900 truncate">{seo.title || '(No title)'}</p>
                          <p className="text-xs text-gray-500 mt-0.5">ID: {seo.id}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {renderFilterBadges(seo.filters)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={seo.is_active ? 'success' : 'gray'}>
                          {seo.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(seo)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDelete(seo.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {data.pages > 1 && (
                <div className="flex justify-center items-center space-x-2 mt-6">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={listFilters.page <= 1}
                    onClick={() => setListFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {listFilters.page} of {data.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={listFilters.page >= data.pages}
                    onClick={() => setListFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No SEO texts</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating your first SEO text.</p>
              <div className="mt-6">
                <Button onClick={() => { setShowForm(true); setFormData(emptyForm) }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add SEO Text
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function FilterCombinationRow({ filter, index, brands, fuelTypes, vehicleTypes, colors, saleStatuses, currentLanguage, onUpdate, onRemove }) {
  const [modelsForBrand, setModelsForBrand] = useState([])
  const [loadingModels, setLoadingModels] = useState(false)

  useEffect(() => {
    if (filter.manufacturer_id) {
      setLoadingModels(true)
      modelsApi.getByManufacturer(filter.manufacturer_id)
        .then(res => {
          const results = res?.data?.data?.results || res?.data?.results || res?.data || []
          setModelsForBrand(Array.isArray(results) ? results : [])
        })
        .catch(() => setModelsForBrand([]))
        .finally(() => setLoadingModels(false))
    } else {
      setModelsForBrand([])
    }
  }, [filter.manufacturer_id])

  return (
    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
      <div className="flex justify-between items-center mb-3">
        <span className="text-sm font-medium text-gray-700">Combination #{index + 1}</span>
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="text-red-500 hover:text-red-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Brand</label>
          <select
            className="input text-sm"
            value={filter.manufacturer_id}
            onChange={(e) => onUpdate(index, 'manufacturer_id', e.target.value)}
          >
            <option value="">Any brand</option>
            {brands.map(b => (
              <option key={b.id} value={b.id}>{b.name || b.name_en}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Model</label>
          <select
            className="input text-sm"
            value={filter.model_group_id}
            onChange={(e) => onUpdate(index, 'model_group_id', e.target.value)}
            disabled={!filter.manufacturer_id || loadingModels}
          >
            <option value="">Any model</option>
            {modelsForBrand.map(m => (
              <option key={m.id} value={m.id}>{m.name || m.name_en}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Year</label>
          <select
            className="input text-sm"
            value={filter.year}
            onChange={(e) => onUpdate(index, 'year', e.target.value)}
          >
            <option value="">Any year</option>
            {yearOptions.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Fuel Type</label>
          <select
            className="input text-sm"
            value={filter.fuel_type_code}
            onChange={(e) => onUpdate(index, 'fuel_type_code', e.target.value)}
          >
            <option value="">Any fuel</option>
            {fuelTypes.map(f => (
              <option key={f.id} value={f.code}>{f.name || f.name_en}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Vehicle Type</label>
          <select
            className="input text-sm"
            value={filter.vehicle_type_id}
            onChange={(e) => onUpdate(index, 'vehicle_type_id', e.target.value)}
          >
            <option value="">Any type</option>
            {vehicleTypes.map(vt => (
              <option key={vt.id} value={vt.id}>{vt.name || vt.name_en}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Color</label>
          <select
            className="input text-sm"
            value={filter.color_id}
            onChange={(e) => onUpdate(index, 'color_id', e.target.value)}
          >
            <option value="">Any color</option>
            {colors.map(c => (
              <option key={c.id} value={c.id}>{c.name || c.name_en}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Sale Status</label>
          <select
            className="input text-sm"
            value={filter.sale_status_id}
            onChange={(e) => onUpdate(index, 'sale_status_id', e.target.value)}
          >
            <option value="">Any status</option>
            {saleStatuses.map(ss => (
              <option key={ss.id} value={ss.id}>{ss.name || ss.name_en}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  )
}
